// stereo-core: stereonet overlay grids (DIPS "Stereonet Overlay": polar, equatorial).
// All lines are true great/small circles, so they are correct in both projections.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3 } from "./vec.js";
import { lineToVec } from "./orient.js";
import { Projection, XY, project } from "./projection.js";
import { greatCircle, smallCircle } from "./circles.js";

export type OverlayKind = "none" | "polar" | "equatorial";

/** Polar net: circles of equal plunge about the vertical, radial lines of equal trend. */
export function polarGrid(p: Projection, step = 10): XY[][] {
  const lines: XY[][] = [];
  const vertical: Vec3 = [0, 0, -1];
  for (let a = step; a < 90; a += step) lines.push(...smallCircle(vertical, a, p, 240));
  // radial trend lines stop short of the centre, as in DIPS, to keep it legible
  const inner = 90 - step;
  for (let t = 0; t < 360; t += step) {
    const seg: XY[] = [];
    for (let pl = 0; pl <= inner; pl += 2) seg.push(project(lineToVec(t, pl), p));
    lines.push(seg);
  }
  return lines;
}

/** Equatorial net: great circles striking N–S (dips every `step`) and
 *  small circles about the N–S horizontal axis. */
export function equatorialGrid(p: Projection, step = 10): XY[][] {
  const lines: XY[][] = [];
  for (let d = step; d <= 90; d += step) {
    lines.push(greatCircle(d, 90, p));
    if (d < 90) lines.push(greatCircle(d, 270, p));
  }
  const ns = lineToVec(0, 0);
  for (let b = step; b < 90; b += step) lines.push(...smallCircle(ns, b, p, 240));
  lines.push(greatCircle(90, 0, p)); // E–W diameter
  return lines;
}

export function overlayGrid(kind: OverlayKind, p: Projection, step = 10): XY[][] {
  return kind === "polar" ? polarGrid(p, step) : kind === "equatorial" ? equatorialGrid(p, step) : [];
}
